// StaffDashboard: Assigned bookings, pending payments, task completion, alerts
import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, Card, CardContent, Chip, Table, TableHead, TableRow, TableCell, TableBody, Button, CircularProgress } from '@mui/material';
import StaffLayout from '../../layouts/StaffLayout';
import api from '../../services/api';
import { useProfile } from '../../context/ProfileContext';

function StaffDashboard() {
  const { profile } = useProfile() || {};
  const user = profile || (typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('user') || '{}') : {});
  const effectiveRole = user && ['booking', 'speaker'].includes(user.role) ? 'staff' : (user && user.role);
  // Permissions-based quick actions
  const perms = Array.isArray(user?.permissions) ? user.permissions : [];
  const canAccessCMS = perms.includes('cms') || effectiveRole === 'admin' || effectiveRole === 'superadmin' || effectiveRole === 'content' || effectiveRole === 'staff';
  const canAccessBookings = perms.includes('booking') || effectiveRole === 'staff' || effectiveRole === 'admin' || effectiveRole === 'superadmin';
  const canAccessTickets = perms.includes('tickets') || effectiveRole === 'staff' || effectiveRole === 'admin' || effectiveRole === 'superadmin';
  const canAccessNotifications = perms.includes('notifications') || effectiveRole === 'staff' || effectiveRole === 'admin' || effectiveRole === 'superadmin';
  const canAccessSpeakers = perms.includes('speaker') || effectiveRole === 'staff' || effectiveRole === 'admin' || effectiveRole === 'superadmin';
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    api.get('/api/dashboard')
      .then(res => setStats(res.data))
      .catch(err => setError(err.response?.data?.error || 'Failed to fetch dashboard stats'))
      .finally(() => setLoading(false));
  }, []);

  // Status colors for badges
  const statusColors = {
    pending: '#eab308',
    approved: '#16a34a',
    rejected: '#f43f5e',
    failed: '#f43f5e',
    completed: '#0ea5a4',
    paid: '#16a34a',
    unpaid: '#eab308',
    processing: '#6366f1',
    sent: '#0ea5e9',
    staff_review: '#6366f1',
    available: '#0ea5a4',
    booked: '#f59e42',
    invited: '#0ea5e9',
    default: '#888'
  };

  // KPI summary cards
  const kpis = [
    { label: 'Assigned Bookings', value: (stats.assignedBookings ?? stats.assigned) || 0, color: '#0ea5a4' },
    { label: 'Approved Bookings', value: (stats.approvedBookings ?? stats.approved) || 0, color: '#16a34a' },
    { label: 'Pending Payments', value: (stats.pendingPayments ?? stats.pendingPaymentsCount) || 0, color: '#eab308' },
    { label: 'Tasks Completed', value: (stats.tasksCompleted ?? stats.tasksDone) || 0, color: '#16a34a' },
    { label: 'Scanned Tickets', value: (stats.scannedTickets ?? stats.scans) || 0, color: '#0ea5a4' },
    { label: 'Unread Notifications', value: (stats.unreadNotifications ?? stats.unreadNotificationsCount) || 0, color: '#f43f5e' },
  ];

  // Booking breakdown table
  const bookingBreakdown = stats.bookings || {};
  const paymentBreakdown = stats.payments || {};

  return (
    <StaffLayout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Staff Dashboard
        </Typography>
        {error && <Typography color="error" mb={2}>{error}</Typography>}
        {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box> : (
          <>
            {/* KPI Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {kpis.map((kpi, idx) => (
                <Grid item xs={12} sm={6} md={3} key={idx}>
                  <Card sx={{ background: '#fff', borderRadius: 3, boxShadow: '0 2px 8px #e6e9ee' }}>
                    <CardContent>
                      <Typography sx={{ color: kpi.color, fontWeight: 700, fontSize: 16 }}>{kpi.label}</Typography>
                      <Typography sx={{ fontWeight: 800, fontSize: 24, color: '#222' }}>{kpi.value ?? 0}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            {/* Quick actions */}
            <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {canAccessBookings && <Button variant="outlined" href="/staff/bookings">Open Bookings</Button>}
              {canAccessTickets && <Button variant="outlined" href="/staff/tickets">Ticket Support</Button>}
              {canAccessNotifications && <Button variant="outlined" href="/staff/notifications">Notifications</Button>}
              {canAccessCMS && <Button variant="outlined" href="/staff/cms">CMS</Button>}
              {canAccessSpeakers && <Button variant="outlined" href="/staff/speakers">Speakers</Button>}
            </Box>

            {/* Booking Breakdown Table with all states */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Booking Status Breakdown</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {['pending','approved','rejected'].map((status, idx) => (
                    <TableRow key={status}>
                      <TableCell>
                        <Chip label={status.charAt(0).toUpperCase() + status.slice(1)} sx={{ background: statusColors[status] || statusColors.default, color: '#fff', fontWeight: 700 }} size="small" />
                      </TableCell>
                      <TableCell>{bookingBreakdown[status] || 0}</TableCell>
                    </TableRow>
                  ))}
                  {/* Show any other statuses as well */}
                  {Object.entries(bookingBreakdown).filter(([status]) => !['pending','approved','rejected'].includes(status)).map(([status, count]) => (
                    <TableRow key={status}>
                      <TableCell>
                        <Chip label={status} sx={{ background: statusColors[status] || statusColors.default, color: '#fff', fontWeight: 700 }} size="small" />
                      </TableCell>
                      <TableCell>{count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            {/* Payment Breakdown Table */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Payment Status Breakdown</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(paymentBreakdown).map(([status, count], idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <Chip label={status} sx={{ background: statusColors[status] || statusColors.default, color: '#fff', fontWeight: 700 }} size="small" />
                      </TableCell>
                      <TableCell>{count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            {/* Alerts and notifications would go here */}
          </>
        )}
      </Box>
    </StaffLayout>
  );
}

export default StaffDashboard;
